/**
 * What oneAI knows about the providers its connector PACKAGES serve — the
 * runtime counterpart of the in-tree tables keyed on `InTreeProvider`.
 *
 * An in-tree provider's capabilities, icon and label are compile-time rows
 * (`shared/space-providers.ts`, `components/spaces/provider-display.ts`). A
 * sandboxed provider's are whatever its pinned manifest declares, so they are
 * data: the backend derives one {@link SandboxedProviderInfo} per installed
 * package (`connectors/sandboxed/provider-info.ts`) and registers them here from
 * the install-set mirror; the frontend registers the same list from the session
 * payload (`auth/check` → `modularProviders`). Every provider-keyed lookup then
 * reads the in-tree table first and this registry second.
 *
 * 🔴 A snapshot, and stale on the safe side. A package installed a moment ago
 * may not be here yet on every instance (the mirror refreshes every 30 s), and
 * an unknown provider resolves to "no capability at all" — a space whose upload
 * button is missing for a few seconds, never one that offers what its package
 * does not do. Nothing that DECIDES a right reads this: rights, tools and
 * confirmation read the installed package itself, per call.
 */

import { type InTreeProvider, isInTreeProvider } from "./in-tree-providers.ts";

/** How a package's corpus behaves for the parts of oneAI that care. */
export type SandboxedCorpus = "files" | "records" | "source";

/** One installed package, as the parts of oneAI that render or gate need it. */
export interface SandboxedProviderInfo {
	provider: string;
	/** The manifest's own name, in both locales oneAI ships. */
	name: { en: string; de: string };
	/**
	 * The package's own icon, SVG markup, and its optional dark-mode variant.
	 * Drawn as an image; absent for a package that ships none.
	 */
	icon?: string;
	iconDark?: string;
	/**
	 * A key into the frontend's icon set, drawn when there is no {@link icon};
	 * unknown keys get a generic icon.
	 */
	iconKey: string;
	/** A Tailwind colour class for the icon's surroundings. */
	colorToken: string;
	/** Whether connecting goes through an OAuth consent bounce. */
	oauth: boolean;
	/**
	 * `files` — a file system (OneDrive, SMB); `records` — rendered provider
	 * records (ClickUp tasks), browsed as a list; `source` — a repository's
	 * files, embedded as source code (an `.html` stays markup, not a page run
	 * through Readability).
	 */
	corpus: SandboxedCorpus;
	capabilities: {
		/**
		 * `false` for a package with no connection: tools only, in every chat.
		 * Nothing to connect, so it is offered by no connect surface.
		 */
		connection: boolean;
		sync: boolean;
		upload: boolean;
		delete: boolean;
		folders: boolean;
		webhooks: boolean;
		tools: boolean;
		/**
		 * Whether a connection can be repaired in place and keep its space — by a
		 * second consent when `oauth`, by new secret values otherwise. Every
		 * caller that bounces through a consent screen must also check `oauth`.
		 */
		reconnect: boolean;
	};
}

let registry: ReadonlyMap<string, SandboxedProviderInfo> = new Map();

/**
 * Replace the registered set. Whole-set on purpose: a package that is no longer
 * installed must disappear, and an incremental API would have to be told.
 */
export const setSandboxedProviders = (
	providers: readonly SandboxedProviderInfo[],
): void => {
	registry = new Map(providers.map((info) => [info.provider, info]));
};

/** The registered package serving `provider`, if there is one. */
export const sandboxedProvider = (
	provider: string,
): SandboxedProviderInfo | undefined => registry.get(provider);

/** Every registered package, in provider order. */
export const sandboxedProviders = (): SandboxedProviderInfo[] =>
	[...registry.values()].sort((a, b) => a.provider.localeCompare(b.provider));

/**
 * Provider ids whose in-tree connector was deleted because a connector package
 * took the id over. Most packages serve the existing spaces they inherited; for
 * `ninjaOne` and `fireflies` nothing was carried over — the takeover migration
 * soft-deleted the in-tree spaces and their owners connect again.
 *
 * The id stays meaningful to the frontend: the brand keeps its look — icon,
 * colours, category — under it, so the package's `iconKey` finds it and every
 * table keyed on looks stays exhaustive over it; and its governance tile keeps
 * the same id. Deleting an in-tree connector moves its id here; it never drops
 * the look.
 */
export const MOVED_PROVIDERS = [
	"oneDrive",
	"smb",
	"weclapp",
	"clickUp",
	"n8n",
	"github",
	"gitlab",
	"forgejo",
	"ninjaOne",
	"outlook",
	"hubspot",
	"fireflies",
	"businessCentral",
	"dynamicsSales",
	"googleDrive",
] as const;

export type MovedProvider = (typeof MOVED_PROVIDERS)[number];

/** Every look a provider can wear: an in-tree provider's, or a moved one's. */
export type ProviderLook = InTreeProvider | MovedProvider;

const MOVED_PROVIDER_SET: ReadonlySet<string> = new Set(MOVED_PROVIDERS);

/** Whether `key` names a look this bundle can draw. */
export const isProviderLook = (key: string): key is ProviderLook =>
	isInTreeProvider(key) || MOVED_PROVIDER_SET.has(key);

/**
 * The look `provider` wears: its own when it is in-tree or moved, else the one a
 * registered package's manifest names as its `iconKey` (every package so far
 * names the brand it serves). Undefined for an id with no look this bundle
 * knows.
 */
export const providerLook = (provider: string): ProviderLook | undefined => {
	if (isProviderLook(provider)) {
		return provider;
	}
	const key = registry.get(provider)?.iconKey;
	return key !== undefined && isProviderLook(key) ? key : undefined;
};
